'use client'

import DarkModeSwitcher from '@/components/DarkModeSwitcher'
import Logo from '@/components/Logo'
import useApp from '@/lib/store'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

export default function Nav({ settings }) {
	const pathname = usePathname()
	const {isContactOpen, setIsContactOpen} = useApp()
	const { navigationLinks } = settings
	const [isNavOpen, setIsNavOpen] = useState(false)
	const [isScrolled, setIsScrolled] = useState(pathname !== '/')
	const [scrollHeight, setScrollHeight] = useState(0)
	const logoRef = useRef<HTMLDivElement>(null)
	const navRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const watchScroll = () => {
			if (window.scrollY > scrollHeight || pathname !== '/') {
				setIsScrolled(true)
			} else {
				setIsScrolled(false)
			}
		}
		window.addEventListener('scroll', watchScroll)
		watchScroll()

		return () => window.removeEventListener('scroll', watchScroll)
	}, [pathname, scrollHeight])

	useEffect(() => {
		if (!logoRef.current || !navRef.current) return

		const updateScrollHeight = () => {
			const logoHeight = logoRef.current?.clientHeight
			setScrollHeight(logoHeight)
		}
		window.addEventListener('resize', updateScrollHeight)
		updateScrollHeight()

		return () => window.removeEventListener('resize', updateScrollHeight)
	}, [])

	// split the navigationLinks into two arrays
	const half = Math.ceil((navigationLinks.length + 1) / 2)
	const leftLinks = navigationLinks.slice(0, half)
	const rightLinks = navigationLinks.slice(half)

	return (
		<>

			<div ref={navRef} className='fixed z-800 top-4 md:top-8 inset-x-4 flex items-center justify-center'>

				<div className={`w-full sm:w-auto flex justify-between sm:justify-normal flex-wrap sm:flex-nowrap items-center px-6 sm:px-8 py-3 sm:py-4 ${isScrolled ? 'gap-[160px] sm:gap-[200px]' : 'gap-4'} transition-all transform-gpu overflow-clip bg-backdrop-light/50 dark:bg-backdrop-dark/50 backdrop-blur-lg backdrop-saturate-120 ${isNavOpen ? "rounded-[8px_8px_0px_0px]" : "rounded-[8px]"} sm:rounded-[8px]`}>

					<Link aria-label='Studio DRAMA' onClick={() => {setIsNavOpen(false); setIsContactOpen(false);}} href='/' className={`absolute top-1/2 left-1/2 -translate-x-1/2 sm:ml-[-19px] flex items-center justify-center h-[11px] sm:h-[14px] ${isScrolled ? 'scale-100 -translate-y-1/2 opacity-100 blur-0' : 'scale-0 translate-y-12 opacity-0 blur-[4px]'} transition transform-gpu`}>
						<Logo className={`w-auto h-[16px] sm:h-[14px] -mr-[0.25em]`} />
					</Link>

					<div className='flex items-center'>
						<button aria-label={isNavOpen ? "Close Menu" : "Open Menu"} onClick={() => setIsNavOpen(!isNavOpen)} className={`relative sm:hidden mr-1 sm:mr-4 inline-flex items-center expand-touch`}>
							<div className={`relative w-4 h-4 grid place-content-center`}>
								<div className={cn(`nav-icon`, {
									"is-active" : isNavOpen
								})} />
							</div>
						</button>
						<div className='hidden sm:flex items-center mr-4 last:mr-0'>
							{leftLinks.map((link) => (
								<Link onClick={() => {setIsNavOpen(false); setIsContactOpen(false);}} key={link?.title} href={link?.url} className='relative group inline-flex items-center mr-4 last:mr-0 mt-[-0.05em] font-500 text-sm leading-100 uppercasexxx -tracking-10 text-black dark:text-white hover:text-black/75 dark:hover:text-white/75 transition'>
									<span className="relative overflow-clip leading-105 transition">
										<span className='relative block group-hover:-translate-y-full transition'>{link.title}</span>
										<span className='absolute block top-full group-hover:-translate-y-[100%] transition' aria-hidden={true}>{link.title}</span>
									</span>
								</Link>
							))}
						</div>
					</div>

					<div className='flex items-center'>
						<div className='hidden sm:flex items-center ml-4 first:ml-0'>
							{rightLinks.map((link) => (
								<Link onClick={() => {setIsNavOpen(false); setIsContactOpen(false);}} key={link?.title} href={link?.url} className='relative group inline-flex items-center ml-4 first:ml-0 mt-[-0.05em] font-500 text-sm leading-100 uppercasexxx -tracking-10 text-black dark:text-white hover:text-black/75 dark:hover:text-white/75 transition'>
									<span className="relative overflow-clip leading-105 transition">
										<span className='relative block group-hover:-translate-y-full transition'>{link.title}</span>
										<span className='absolute block top-full group-hover:-translate-y-[100%] transition' aria-hidden={true}>{link.title}</span>
									</span>
								</Link>
							))}
							<button onClick={() => {setIsContactOpen(!isContactOpen); setIsNavOpen(false)}} className='relative group inline-flex items-center ml-4 first:ml-0 mt-[-0.05em] font-500 text-sm leading-100 uppercasexxx -tracking-10 text-black dark:text-white hover:text-black/75 dark:hover:text-white/75 transition [body:has(.contact-is-open)_&]:opacity-40'>
								<span className="relative overflow-clip leading-105 transition">
									<span className='relative block group-hover:-translate-y-full transition'>Contact</span>
									<span className='absolute block top-full group-hover:-translate-y-[100%] transition' aria-hidden={true}>Contact</span>
								</span>
							</button>
						</div>
						<div className="ml-1 sm:ml-4">
							<DarkModeSwitcher />
						</div>
					</div>

				</div>

				<div className={`sm:hidden absolute top-full left-0 z-700 w-full grid bg-backdrop-light/50 dark:bg-backdrop-dark/50 backdrop-blur-[8px] backdrop-saturate-120 rounded-[0px_0px_8px_8px] transition-[grid-template-rows] overflow-hidden ${isNavOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
					<div className="w-full overflow-hidden">
						<div className="px-6 py-12">

							<div className='flex flex-col gap-1'>
								{navigationLinks.map((link, index) => (
									<Link onClick={() => {setIsContactOpen(false); setIsNavOpen(false)}} key={link?.title} href={link?.url} className={`font-400 text-2xl leading-140 -tracking-10'}`}>{link.title}</Link>
								))}
								<button onClick={() => {setIsContactOpen(true); setIsNavOpen(false)}} className={`font-400 text-2xl leading-140 -tracking-10 text-left block'}`}>Contact</button>
							</div>

						</div>
					</div>
				</div>

			</div>

			<button onClick={() => {setIsNavOpen(false); setIsContactOpen(false);}} className={`sm:hidden absolute inset-0 z-500 bg-white/60 dark:bg-black/60 transition-opacity duration-500 ${isNavOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}><span className="sr-only">Close Menu</span></button>

			<div ref={logoRef} className={`home-intro-logo fixed z-300 bottom-0 w-full px-4 sm:px-6 md:px-8 pb-4 sm:pb-6 md:pb-8 ${isScrolled ? 'scale-75 opacity-0' : 'scale-100 opacity-100'} origin-bottom transition transform-gpu`}>
				{/* <Logo className={`w-full h-auto dark:text-white text-black`} /> */}
				<div className="text-[10.1vw] md:text-[10.25vw] xl:text-[10.42vw] font-drama leading-[0.72] whitespace-nowrap text-center [font-variation-settings:'wght'_996!important]">
					<span className="inline-block ml-[-0.03em]">D</span>
					<span className="inline-block ml-[-0.05em]">R</span>
					<span className="inline-block ml-[-0.085em]">A</span>
					<span className="inline-block ml-[0.025em]">M</span>
					<span className="inline-block ml-[0.02em]">A</span>
				</div>
			</div>

		</>
	)
}
